import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Edit, Loader2, MoreHorizontal, Palette, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { BrandKitCardPreview } from '@/components/brand-kits/BrandKitCardPreview';
import { templateEditPath } from '@/lib/brand-kit/templateRoutes';

interface BrandKitListingCardProps {
  id: string;
  name: string;
  isDefault: boolean;
  data: unknown;
  previewVideoUrl?: string | null;
  previewPosterUrl?: string | null;
  deleting: boolean;
  updating: boolean;
  onSetDefault: (id: string) => void;
  onDelete: (id: string) => void;
}

export function BrandKitListingCard({
  id,
  name,
  isDefault,
  data,
  previewVideoUrl,
  previewPosterUrl,
  deleting,
  updating,
  onSetDefault,
  onDelete,
}: BrandKitListingCardProps) {
  const { t } = useTranslation();
  const href = templateEditPath(id);

  return (
    <article className="group relative overflow-hidden rounded-xl bg-card ring-1 ring-border/70 transition-shadow hover:shadow-lg hover:ring-border">
      <Link
        to={href}
        aria-label={`${name} — ${t('brandKits.edit')}`}
        className="absolute inset-0 z-10 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      />
      <BrandKitCardPreview
        kitData={data}
        previewVideoUrl={previewVideoUrl}
        previewPosterUrl={previewPosterUrl}
      />
      <div className="relative px-3 pb-3 pt-2.5">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold leading-tight text-foreground">
              {name}
            </h3>
            {isDefault && (
              <Badge variant="default" className="mt-1 h-5 px-1.5 text-[10px]">
                {t('brandKits.default')}
              </Badge>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative z-20 h-8 w-8 shrink-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={href} className="flex w-full items-center gap-2">
                  <Edit className="h-4 w-4" />
                  {t('brandKits.edit')}
                </Link>
              </DropdownMenuItem>
              {!isDefault && (
                <DropdownMenuItem
                  onClick={() => onSetDefault(id)}
                  disabled={updating}
                >
                  {updating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Palette className="mr-2 h-4 w-4" />
                  )}
                  {updating ? t('brandKits.setting') : t('brandKits.setAsDefault')}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    className="text-destructive focus:text-destructive"
                    disabled={deleting}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {deleting ? t('brandKits.deleting') : t('brandKits.delete')}
                  </DropdownMenuItem>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('brandKits.confirmDelete')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {isDefault
                        ? t('brandKits.confirmDeleteDefault', { name })
                        : t('brandKits.confirmDeleteNormal', { name })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('brandKits.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onDelete(id)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      disabled={deleting}
                    >
                      {deleting ? t('brandKits.deleting') : t('brandKits.delete')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <Link
          to={href}
          className="relative z-20 mt-2 inline-flex h-8 items-center gap-1.5 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          <Edit className="h-3.5 w-3.5" />
          {t('brandKits.edit')}
        </Link>
      </div>
    </article>
  );
}
